<template>
    <div class="book-slider-container" :style="containerStyle">
        <div class="background-overlay"></div>
        <div class="content-wrapper">
            <h2 class="slider-title">{{ title }}</h2>
            <swiper
                :modules="modules"
                :slides-per-view="7"
                :space-between="10"
                :centeredSlides="true"
                navigation
                :effect="'coverflow'"
                :coverflowEffect="{
                    rotate: 45,
                    stretch: 0,
                    depth: 200,
                    modifier: 1,
                    slideShadows: true,
                }"
                :loop="true"
                :autoplay="{
                    delay: 2000,
                    disableOnInteraction: false,
                }"
                @slideChange="onSlideChange"
                class="book-swiper"
            >
                <swiper-slide v-for="book in books" :key="book.id" class="book-slide">
                    <Book3D
                        :id="book.id"
                        :front-cover="book.banners"
                        :slug="book.slug"
                        :back-cover="book.backCover"
                        :spine="book.banners"
                        :title="book.title"
                        :width="book.width || 180"
                        :height="book.height || 250"
                        :spine-width="book.spineWidth || 35"
                    />
                </swiper-slide>
            </swiper>

        </div>
    </div>
</template>

<script setup>
import {A11y, Autoplay, EffectCoverflow, Navigation, Pagination, Scrollbar} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/vue'
// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/scrollbar'
import 'swiper/css/effect-coverflow'
import Book3D from './Book3D.vue'
import {NotificationToast} from "@/utils/notification.js";
import {onMounted, ref, computed} from "vue";

// Props du composant
const props = defineProps({
    title: {
        type: String,
        default: 'Top 10 de la semaine'
    },
    autoplay: {
        type: Boolean,
        default: true
    }
})

const books = ref([])
const activeBookIndex = ref(0)

const emit = defineEmits(['bookSelected', 'slideChange'])

const containerStyle = computed(() => {
    if (books.value.length > 0 && books.value[activeBookIndex.value]) {
        const activeBook = books.value[activeBookIndex.value]
        return {
            backgroundImage: 'linear-gradient(45deg,#2A3993,#ff7e41)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
        }
    }
    return {
        background: 'linear-gradient(45deg,#2A3993,#ff7e41)'
    }
})

const formatDate = (date) => {
    if (!date) return ''
    return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
}

const loadBooks = async () => {
    try {
        const response = await axios.get('/api/popular')
        if(response.data){
            books.value = response.data
        }
    } catch (error){
        NotificationToast('error', error.response.data.message)
    }
}

const onSlideChange = (swiper) => {
    activeBookIndex.value = swiper.realIndex
    const activeBook = books.value[swiper.realIndex]
    emit('slideChange', {index: swiper.realIndex, book: activeBook})
}

onMounted(() => {
    loadBooks()
})

// Modules Swiper
const modules = [Navigation, Pagination, Scrollbar, A11y, EffectCoverflow, Autoplay]
</script>

<style scoped>
.book-slider-container {
    width: 100%;
    padding: 1rem;
    min-height: 70vh;
    max-height: 800px;
    position: relative;
    transition: background-image 0.5s ease-in-out;
}

.background-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    backdrop-filter: blur(25px);
    background: rgba(0, 0, 0, 0.6);
    z-index: 1;
}

.content-wrapper {
    position: relative;
    z-index: 2;
    height: 100%;
}

.slider-title {
    text-align: center;
    font-size: 2.2rem;
    font-weight: bold;
    color: white;
    margin-bottom: 2rem;
    text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.8);
    letter-spacing: 1px;
}

.book-swiper {
    width: 100%;
    padding: 80px 0;
    height: 400px;
    margin-bottom: 40px;
}

.book-slide {
    width: 180px;
    height: 250px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    transform-origin: center;
}

/* Slide actif - agrandi et décalé vers la gauche */
:deep(.swiper-slide-active) {
    transform: scale(1.3) translateX(-30px) !important;
    z-index: 10;
    filter: brightness(1.2);
}

:deep(.swiper-slide-active .book-3d) {
    box-shadow: 0 15px 40px rgba(255, 255, 255, 0.3),
    0 0 20px rgba(255, 255, 255, 0.2),
    inset 0 0 0 2px rgba(255, 255, 255, 0.6);
    border-radius: 8px;
}

/* Slides précédent et suivant - taille moyenne */
:deep(.swiper-slide-prev),
:deep(.swiper-slide-next) {
    transform: scale(0.85) !important;
    z-index: 8;
    opacity: 0.8;
    filter: brightness(0.7);
}

/* Slides éloignés - plus petits et moins visibles */
:deep(.swiper-slide:not(.swiper-slide-active):not(.swiper-slide-prev):not(.swiper-slide-next)) {
    transform: scale(0.6) !important;
    z-index: 5;
    opacity: 0.4;
    filter: brightness(0.4);
}

/* Bloc meta en bas à gauche */
.book-meta-info {
    position: absolute;
    bottom: 40px;
    left: 60px;
    max-width: 400px;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(10px);
    padding: 24px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.8);
}

.book-meta-title {
    font-size: 1.8rem;
    font-weight: bold;
    color: #ffffff;
    margin-bottom: 8px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.9);
    line-height: 1.2;
}

.book-meta-author {
    font-size: 1.1rem;
    color: #e0e0e0;
    margin-bottom: 6px;
    font-style: italic;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
}

.book-meta-date {
    font-size: 1rem;
    color: #cccccc;
    margin-bottom: 6px;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
}

.book-meta-genre {
    font-size: 0.95rem;
    color: #b8b8b8;
    font-weight: 500;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Navigation Swiper - style PS3 */
:deep(.swiper-button-next),
:deep(.swiper-button-prev) {
    color: rgba(255, 255, 255, 0.9);
    background: rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    width: 55px;
    height: 55px;
    margin-top: -27px;
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.3s ease;
}

:deep(.swiper-button-next:hover),
:deep(.swiper-button-prev:hover) {
    background: rgba(255, 255, 255, 0.25);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.5);
    transform: scale(1.1);
}

:deep(.swiper-button-next) {
    right: 30px;
}

:deep(.swiper-button-prev) {
    left: 30px;
}

/* Responsive */
@media (max-width: 1200px) {
    .book-swiper {
        --swiper-navigation-size: 25px;
    }

    :deep(.swiper-slide-active) {
        transform: scale(1.2) translateX(-20px) !important;
    }

    .book-meta-info {
        left: 30px;
        bottom: 30px;
        max-width: 350px;
        padding: 20px;
    }

    .book-meta-title {
        font-size: 1.6rem;
    }
}

@media (max-width: 768px) {
    .book-slider-container {
        min-height: 60vh;
        padding: 0.5rem;
    }

    .book-swiper {
        height: 320px;
        padding: 60px 0;
    }

    .book-slide {
        width: 140px;
        height: 200px;
    }

    :deep(.swiper-slide-active) {
        transform: scale(1.15) translateX(-15px) !important;
    }

    .slider-title {
        font-size: 1.8rem;
        margin-bottom: 1.5rem;
    }

    .book-meta-info {
        left: 20px;
        bottom: 20px;
        max-width: calc(100% - 40px);
        padding: 16px;
    }

    .book-meta-title {
        font-size: 1.4rem;
    }

    .book-meta-author,
    .book-meta-date {
        font-size: 1rem;
    }

    .book-meta-genre {
        font-size: 0.9rem;
    }
}

@media (max-width: 480px) {
    .book-swiper {
        --swiper-navigation-size: 20px;
    }

    :deep(.swiper-button-next),
    :deep(.swiper-button-prev) {
        width: 45px;
        height: 45px;
        margin-top: -22px;
    }

    :deep(.swiper-button-next) {
        right: 15px;
    }

    :deep(.swiper-button-prev) {
        left: 15px;
    }
}
</style>
